import { useState, type ReactNode } from "react";
import { CalendarClock } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import type { ReleaseDetail } from "../../shared/types/api";
import { Button, Card, CardBody, CardHeader, ProgressBar } from "../../shared/ui";
import { describeScope, formatReleaseMoment } from "./releaseVocabulary";
import { ReleaseFieldsCard } from "./ReleaseFieldsCard";
import { ScheduleReleaseDrawer } from "./ScheduleReleaseDrawer";

/**
 * §18's summary: who owns this, when it goes, and what is in it.
 *
 * <p>The scope figures are counted by the server from the live mappings rather than stored per
 * type, so they cannot drift from the scope tab sitting one click away.
 */
export function ReleaseOverviewTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [scheduling, setScheduling] = useState(false);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Scope" subtitle={describeScope(release.scope.completed, release.scope.total)} />
        <CardBody className="space-y-4">
          <ProgressBar
            value={release.scope.completed}
            total={release.scope.total}
            tone={release.scope.completed === release.scope.total ? "green" : "blue"}
            label="Complete"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label="Stories" value={release.scope.stories} />
            <Figure label="Tasks" value={release.scope.tasks} />
            <Figure label="Bugs" value={release.scope.bugs} />
            <Figure label="Subtasks" value={release.scope.subtasks} />
          </div>
          {(release.scope.addedAfterFreeze > 0 || release.scope.removedAfterFreeze > 0) && (
            <div className="flex flex-wrap gap-4 rounded-lg bg-ink-50 px-3 py-2 text-xs">
              {/* §13's headline figures. A frozen scope that moved is not a failure, but it is
                  the thing a release review opens the page to find. */}
              <span className="text-ink-700">
                <span className="font-medium text-amber-700">
                  +{release.scope.addedAfterFreeze}
                </span>{" "}
                added after freeze
              </span>
              <span className="text-ink-700">
                <span className="font-medium text-blue-700">
                  −{release.scope.removedAfterFreeze}
                </span>{" "}
                removed
              </span>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Schedule"
          subtitle={`Times shown in ${release.timeZone}.`}
          action={
            can("RELEASE_EDIT") && (
              <Button
                size="sm"
                variant="secondary"
                icon={<CalendarClock className="h-4 w-4" />}
                onClick={() => setScheduling(true)}
              >
                {release.plannedReleaseAt ? "Reschedule" : "Set the date"}
              </Button>
            )
          }
        />
        <CardBody className="space-y-2">
          <Row label="Planned release" value={formatReleaseMoment(release.plannedReleaseAt, release.timeZone)} />
          <Row
            label="Deployment window"
            value={
              release.deploymentWindowStart
                ? `${formatReleaseMoment(release.deploymentWindowStart, release.timeZone)} → ${formatReleaseMoment(release.deploymentWindowEnd, release.timeZone)}`
                : "Not set"
            }
          />
          <Row
            label="Planned downtime"
            value={
              release.plannedDowntimeMinutes == null
                ? "None"
                : `${release.plannedDowntimeMinutes} minutes`
            }
          />
          <Row
            label="Actually released"
            value={formatReleaseMoment(release.actualReleaseAt, release.timeZone)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="People" />
        <CardBody className="space-y-2">
          <Row label="Release owner" value={release.releaseOwner?.fullName ?? "—"} />
          <Row label="Release manager" value={release.releaseManager?.fullName ?? "—"} />
          <Row label="Technical lead" value={release.technicalLead?.fullName ?? "—"} />
          <Row label="QA lead" value={release.qaLead?.fullName ?? "—"} />
          <Row label="Deployment owner" value={release.deploymentOwner?.fullName ?? "—"} />
        </CardBody>
      </Card>

      {(release.description || release.businessSummary || release.knownLimitations) && (
        <Card>
          <CardHeader title="What this is for" />
          <CardBody className="space-y-3">
            <Detail label="Description" value={release.description} />
            <Detail label="Business summary" value={release.businessSummary} />
            <Detail label="Objective" value={release.releaseObjective} />
            <Detail label="Business impact" value={release.businessImpact} />
            <Detail label="Customer impact" value={release.customerImpact} />
            <Detail label="Known limitations" value={release.knownLimitations} />
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Build" subtitle="Maintained by hand until source control is connected." />
        <CardBody className="space-y-2">
          <Row label="Source branch" value={release.sourceBranch ?? "—"} mono />
          <Row label="Release branch" value={release.releaseBranch ?? "—"} mono />
          <Row label="Git tag" value={release.gitTag ?? "—"} mono />
          <Row label="Build" value={release.buildNumber ?? "—"} mono />
          <Row label="Artifact" value={release.artifactVersion ?? "—"} mono />
        </CardBody>
      </Card>
      {/* §58-§61: whatever else this organisation records about a release. */}
      <ReleaseFieldsCard release={release} onChanged={onChanged} />

      <ScheduleReleaseDrawer
        release={release}
        open={scheduling}
        onClose={() => setScheduling(false)}
        onSaved={onChanged}
      />
    </div>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-ink-50 px-3 py-2">
      <p className="tnum text-lg font-semibold text-ink-900">{value}</p>
      <p className="text-xs text-ink-500">{label}</p>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-ink-500">{label}</span>
      <span className={mono ? "font-mono text-xs text-ink-900" : "text-ink-900"}>{value}</span>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return null;
  }
  return (
    <div>
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-800">{value}</p>
    </div>
  );
}
